import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertQuestionMappings,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = 'all'
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 以下皆為 1-based 寫法，convertVisibleIf 會自動 -1 還原為 0-based。
  // PDF 題號 ↔ JSON 題號：9a-9d=q22-q25、12=q29、13=q31、14-20=q32-q41、21-27=q42-q50、
  //   30=q53、31=q54、33-1~3=q56-q58、34=q59、35A-E=q60-q69（每組「有沒有搭乘」+「困難」）、
  //   36a=q71、37a=q73、38/39=q74/q75、40=q76、41=q78、41O=q79、41a=q80、42=q81-q88、
  //   43-51=q89-q104、60=q119、63=q126、64=q129、68=q139、70=q156、72=q161、
  //   73=q163-q168、75=q171-q176、76=q177、78=q180、82=q184、F=q188、F1-F3=q189-q191。

  // E1 舊制障礙類別(q7) 第9項＝植物人：第12題、陸章工作現況免填答，且第72題後結束訪查
  const notPlantPerson = '{q7} <> 9'
  // 2題 出生年(q10)：第1項是民國0年(115歲)、第116項是民國115年(0歲)，
  // 故「足歲年齡 = 116 - 選項序號」，年齡門檻一律換算成序號區間。
  const isAge3AndUp = '{q10} <= 113' // 3歲以上（未滿3歲＝114-116項）
  const isAge3To5 = '({q10} >= 111 and {q10} <= 113)' // 3歲至未滿6歲
  const isAge6AndUp = '{q10} <= 110' // 6歲以上
  const isAge12AndUp = '{q10} <= 104' // 12歲及以上
  const isAge15AndUp = '{q10} <= 101' // 15歲以上（工作現況）
  const isAge25To74 = '({q10} >= 42 and {q10} <= 91)' // 25歲至未滿75歲（癌症篩檢）
  // 76題 限民國93年1月13日前(含當日)出生；JSON 只到「月」，93年1月整月視為符合
  const bornBeforeElectionDay = '(({q10} <= 93) or ({q10} = 94 and {q11} = 1))'
  // 13題 居住地點：(1)一般住宅續問 14-20 題；(2)住宿式機構續問 21-27 題
  const livesAtHome = '{q31} = 1'
  const livesInFacility = '{q31} = 2'
  // 30題 (5)都沒有外出者，31-33 題免答
  const goesOut = '({q53} = 1 or {q53} = 2 or {q53} = 3 or {q53} = 4)'
  // 40題 (1)可以獨立自我照顧 → 跳問 52 題，故 40a-51 題都要「非(1)」
  const needsCare = '({q76} = 2 or {q76} = 3)'
  // 43-51 題另限「住一般住宅者填答」
  const homeServiceSection = '(' + livesAtHome + ') and ' + needsCare
  // 陸章 60-62 題：15歲以下、障礙類別為植物人者免填答
  const workSection = '(' + notPlantPerson + ') and (' + isAge15AndUp + ')'
  // 19題 12 項設施任一選「(2)需要」→ 續問 19a
  const needsAnyFacility =
    '({q38.1} = 2) or ({q38.2} = 2) or ({q38.3} = 2) or ({q38.4} = 2) or ' +
    '({q38.5} = 2) or ({q38.6} = 2) or ({q38.7} = 2) or ({q38.8} = 2) or ' +
    '({q38.9} = 2) or ({q38.10} = 2) or ({q38.11} = 2) or ({q38.12} = 2)'
  // 41a 填(1)~(12)家人代碼者續答 42 題；填(13)~(22)者跳答 43 題
  const mainCarerIsFamily = '({q80} >= 1 and {q80} <= 12)'

  const visibleIfConditionsInit = convertVisibleIf({
    // 7.1 未滿3歲不用填答；3歲至未滿6歲填學齡前兩項(q16)，6歲以上填(3)~(10)(q17)
    q16: isAge3To5,
    q17: isAge6AndUp,
    // 8. (2)有配偶/(3)與人同居 → 續問 8a、8b；(1)未婚等其餘選項跳問第 9 題
    q19: '{q18} = 2 or {q18} = 3',
    q20: '{q18} = 2 or {q18} = 3',
    // 9. (2)有子女 → 續問 9a-9d 四個人數欄
    q22: '{q21} = 2',
    q23: '{q21} = 2',
    q24: '{q21} = 2',
    q25: '{q21} = 2',
    // 12. 3歲以下、障礙類別為植物人者免填答，跳問第 13 題
    q29: '(' + notPlantPerson + ') and (' + isAge3AndUp + ')',
    q30: '(' + notPlantPerson + ') and (' + isAge3AndUp + ')',
    // 14-20. 限 13 題選(1)一般住宅
    q32: livesAtHome,
    q33: livesAtHome,
    q34: livesAtHome,
    q35: livesAtHome,
    q36: livesAtHome,
    // 18. (2)與他人同住 是 o_type 6，值為 "other"，不可寫成 2
    q37: '(' + livesAtHome + ') and ({q36} = "other")',
    q38: livesAtHome,
    // 19. 第 12 列「其他」需要時，才問是哪種設施
    q39: '(' + livesAtHome + ') and ({q38.12} = 2)',
    // 19a. 逐列對應 19 題：該列選「(2)需要」，19a 才出現同一列
    q40: '(' + livesAtHome + ') and (' + needsAnyFacility + ')',
    'q40.row1': '{q38.1} = 2',
    'q40.row2': '{q38.2} = 2',
    'q40.row3': '{q38.3} = 2',
    'q40.row4': '{q38.4} = 2',
    'q40.row5': '{q38.5} = 2',
    'q40.row6': '{q38.6} = 2',
    'q40.row7': '{q38.7} = 2',
    'q40.row8': '{q38.8} = 2',
    'q40.row9': '{q38.9} = 2',
    'q40.row10': '{q38.10} = 2',
    'q40.row11': '{q38.11} = 2',
    'q40.row12': '{q38.12} = 2',
    q41: livesAtHome,
    // 21-27. 限 13 題選(2)住宿式機構
    q42: livesInFacility,
    q43: livesInFacility,
    q44: livesInFacility,
    // 23. (1)沒有家人或親屬 → 跳問第 25 題；(2)有 → 續問 23a、24
    q45: '(' + livesInFacility + ') and ({q44} = 2)',
    q46: '(' + livesInFacility + ') and ({q44} = 2)',
    q47: livesInFacility,
    q48: livesInFacility,
    q49: livesInFacility,
    q50: livesInFacility,
    // 31-33. 30 題選(5)都沒有外出者免答
    q54: goesOut,
    q55: goesOut,
    q56: goesOut,
    q57: goesOut,
    q58: goesOut,
    // 34. 限 30 題選(4)很少外出或(5)都沒有外出
    q59: '{q53} = 4 or {q53} = 5',
    // 35A-E. 各組先問「(01)有搭乘」，有搭乘才問困難題
    q61: '{q60} = 1',
    q63: '{q62} = 1',
    q65: '{q64} = 1',
    q67: '{q66} = 1',
    q69: '{q68} = 1',
    // 36. (1)有使用通用計程車 → 續問 36a
    q71: '{q70} = 1',
    // 37. (1)有申請復康巴士 → 續問 37a
    q73: '{q72} = 1',
    // 38、39. 未滿6歲者免答，跳問第 40 題
    q74: isAge6AndUp,
    q75: isAge6AndUp,
    // 40. (1)可以獨立自我照顧 → 跳問第 52 題
    q77: needsCare,
    q78: needsCare,
    // 41O. 41 題勾選(12)其他親戚 → 續問說明
    q79: needsCare + ' and ({q78} anyof [12])',
    q80: needsCare,
    // 42. 41a 填(1)~(12)家人代碼者續答 42 題；填(13)~(22)者跳答 43 題
    q81: needsCare + ' and ' + mainCarerIsFamily,
    q82: needsCare + ' and ' + mainCarerIsFamily,
    q83: needsCare + ' and ' + mainCarerIsFamily,
    q84: needsCare + ' and ' + mainCarerIsFamily,
    q85: needsCare + ' and ' + mainCarerIsFamily,
    q86: needsCare + ' and ' + mainCarerIsFamily,
    q87: needsCare + ' and ' + mainCarerIsFamily,
    q88: needsCare + ' and ' + mainCarerIsFamily,
    // 43-51. 住一般住宅者填答，未住一般住宅者跳問第 52 題
    q89: homeServiceSection,
    // 43. (1)不需要居家服務 → 跳問第 46 題（43a-43d、44、45 皆不問）
    q90: homeServiceSection + ' and ({q89} = 2)',
    q91: homeServiceSection + ' and ({q89} = 2)',
    q92: homeServiceSection + ' and ({q89} = 2)',
    q93: homeServiceSection + ' and ({q89} = 2)',
    q94: homeServiceSection + ' and ({q89} = 2)',
    // 44. (1)有申請且符合需要 → 跳問第 46 題，故第 45 題限(2)(3)
    q95: homeServiceSection + ' and ({q89} = 2) and ({q94} = 2 or {q94} = 3)',
    q96: homeServiceSection,
    // 46. (1)不需要日間服務 → 跳問第 49 題（46a-46c、47、48 皆不問）
    q97: homeServiceSection + ' and ({q96} = 2)',
    q98: homeServiceSection + ' and ({q96} = 2)',
    q99: homeServiceSection + ' and ({q96} = 2)',
    q100: homeServiceSection + ' and ({q96} = 2)',
    // 47. (1)有申請且符合需要 → 跳問第 49 題，故第 48 題限(2)(3)
    q101:
      homeServiceSection + ' and ({q96} = 2) and ({q100} = 2 or {q100} = 3)',
    q102: homeServiceSection,
    // 49. (1)有申請且符合需要 → 跳問第 51 題，故第 50 題限(2)(3)
    q103: homeServiceSection + ' and ({q102} = 2 or {q102} = 3)',
    q104: homeServiceSection,
    // 60-62. 15歲以下、障礙類別為植物人者免填答，跳問第 63 題
    q119: workSection,
    // 60. (1)有在做工作 → 續問 60a，答完跳問第 62 題
    q120: workSection + ' and ({q119} = 1)',
    // 60. (2)無工作但想工作且在找工作 → 續問 60b
    q121: workSection + ' and ({q119} = 2)',
    // 60b. (2)不能立刻開始工作 → 續問原因
    q122: workSection + ' and ({q119} = 2) and ({q121} = 2)',
    // 60. (5)不想工作 → 續問 60c，答完跳問第 63 題
    q123: workSection + ' and ({q119} = 5)',
    // 61. 只有 60 題選(2)或(3)者會走到；(1)跳 62、(4)跳 63、(5)走 60c 後跳 63
    q124: workSection + ' and ({q119} = 2 or {q119} = 3)',
    // 62. 只有 60 題選(1)有在做工作者作答
    q125: workSection + ' and ({q119} = 1)',
    // 63. (1)有做 → 63a；(2)沒有做過 → 63b；(98)不知道 → 跳問第 64 題
    q127: '{q126} = 1',
    q128: '{q126} = 2',
    // 64. 未滿25歲或75歲以上者免答，跳問第 65 題
    q129: isAge25To74,
    // 64. (1)有做 → 64a；(2)沒有做過 → 64b；(98)不知道 → 跳問第 65 題
    q130: isAge25To74 + ' and ({q129} = 1)',
    q131: isAge25To74 + ' and ({q129} = 2)',
    // 65. (1)需要定期就醫 → 續問 65a；(2)不需要 → 跳問第 66 題
    q133: '{q132} = 1',
    // 66. (2)需要復健治療 → 續問 66a、66b；(1)不需要 → 跳問第 67 題
    q135: '{q134} = 2',
    q136: '{q134} = 2',
    // 67. (2)診所 → 續問 67a
    q138: '{q137} = 2',
    // 68. (1)完全沒有困難 → 跳問第 69 題；68a1-3、68b1-3 醫院/診所/衛生所三組都問
    q140: '{q139} = 2',
    q141: '{q139} = 2',
    q142: '{q139} = 2',
    q143: '{q139} = 2',
    // 68b 勾(3)缺乏無障礙環境 → 續問項目；勾(6)缺乏溝通支持服務 → 續問說明與建議
    q144: '({q139} = 2) and ({q143} anyof [3])',
    q145: '({q139} = 2) and ({q143} anyof [6])',
    q146: '({q139} = 2) and ({q143} anyof [6])',
    q147: '{q139} = 2',
    q148: '({q139} = 2) and ({q147} anyof [3])',
    q149: '({q139} = 2) and ({q147} anyof [6])',
    q150: '({q139} = 2) and ({q147} anyof [6])',
    q151: '{q139} = 2',
    q152: '({q139} = 2) and ({q151} anyof [3])',
    q153: '({q139} = 2) and ({q151} anyof [6])',
    q154: '({q139} = 2) and ({q151} anyof [6])',
    // 70. (1)需要且有使用 → 70a；(2)需要但未使用 → 70b、70c
    q157: '{q156} = 1',
    q158: '{q156} = 2',
    q159: '{q156} = 2',
    // 72. (4)不好或(5)很不好 → 續問 72a
    q162: '{q161} = 4 or {q161} = 5',
    // 73 題以後：障礙類別為植物人者於第 72 題後結束訪查
    q163: notPlantPerson,
    q164: notPlantPerson,
    // 73-2a~73-2d. 73-2 該列選「(4)無」→ 續問該活動不想參與的主要原因
    q165: '(' + notPlantPerson + ') and ({q164.1} = 2)',
    q166: '(' + notPlantPerson + ') and ({q164.2} = 2)',
    q167: '(' + notPlantPerson + ') and ({q164.3} = 2)',
    q168: '(' + notPlantPerson + ') and ({q164.4} = 2)',
    q169: notPlantPerson,
    // 74. (2)有遭遇困難 → 續問 74a
    q170: '(' + notPlantPerson + ') and ({q169} = 2)',
    q171: notPlantPerson,
    q172: notPlantPerson,
    // 75-2a~75-2d. 75-2 該列選「(2)沒有」→ 續問該社團不想參與的主要原因
    q173: '(' + notPlantPerson + ') and ({q172.1} = 2)',
    q174: '(' + notPlantPerson + ') and ({q172.2} = 2)',
    q175: '(' + notPlantPerson + ') and ({q172.3} = 2)',
    q176: '(' + notPlantPerson + ') and ({q172.4} = 2)',
    // 76. 限民國93年1月13日前(含當日)出生者作答
    q177: '(' + notPlantPerson + ') and ' + bornBeforeElectionDay,
    // 76. (2)沒有去投票 → 續問 76a
    q178: '(' + notPlantPerson + ') and ({q177} = 2)',
    // 77. (3)沒有投票權者跳問第 78 題
    q179: '(' + notPlantPerson + ') and ({q177} = 1 or {q177} = 2)',
    // 78. 限12歲及以上作答
    q180: '(' + notPlantPerson + ') and (' + isAge12AndUp + ')',
    q181: notPlantPerson,
    q182: notPlantPerson,
    q183: notPlantPerson,
    // 82. (2)不願意回答 → 82a-82c 皆不顯示，問卷自然結束
    q184: notPlantPerson,
    // 82a. 僅調查女性(第 1 題選(2)女)，其餘直接跳答 82b
    q185: '(' + notPlantPerson + ') and ({q184} = 1) and ({q9} = 2)',
    // 82b. 男性直接作答；女性限 82a 選(2)沒有（選(1)有則跳 82c）
    q186:
      '(' +
      notPlantPerson +
      ') and ({q184} = 1) and (({q9} <> 2) or ({q185} = 2))',
    // 82c. 82a 或 82b 任一選(1)有
    q187:
      '(' +
      notPlantPerson +
      ') and ({q184} = 1) and (({q185} = 1) or ({q186} = 1))',
    // F. (3)大部分非身心障礙者本人回答 → 續問 F1~F3
    // 訪員填寫欄位不受「植物人結束訪查」影響，一律作答
    q189: '{q188} = 3',
    q190: '{q188} = 3',
    q191: '{q188} = 3',
  })
  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null
  const triggersConditionsInit = null
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 本案為政府委託訪問調查，結語採客戶指定文案，不使用 EZChoice 預設結語
  const endSurveyMsg = (survey) => {
    setCompletionMessage(`<div>問卷已成功送出，請致贈受訪者禮券，謝謝。</div>`)
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving:
      requiresLogin.login || webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
